import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { IFormState } from '../../../common/base-detail.component';
import { EntityTranslationsWidgetComponent, TranslationEdit } from '../entity-translations-widget/entity-translations-widget.component';

/**
 * Hosts the translation editor in a formly tab. Bind it to the record id (`key: 'id'`)
 * and name the registered entity via `props.entity`.
 *
 * Translations are not formly-bound fields, so edits are written onto the detail model directly and
 * the detail component is asked to re-check dirtiness. The entity's own save mutation then persists
 * them along with the rest of the form - one save button, as everywhere else in the app.
 */
@Component({
  selector: 'app-formly-entity-translations-widget',
  templateUrl: './formly-entity-translations-widget.component.html',
  standalone: true,
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    EntityTranslationsWidgetComponent
  ]
})
export class FormlyEntityTranslationsWidgetComponent extends FieldType<FieldTypeConfig> implements OnInit {

  widget = viewChild(EntityTranslationsWidgetComponent);

  private readonly destroyRef = inject(DestroyRef);

  private originalTranslations: unknown[];

  ngOnInit(): void {
    this.detailComponent?.onSaved
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.originalTranslations = undefined;
        void this.widget()?.reload();
      });
  }

  /** Only edited languages are sent - the differ leaves absent ones untouched. */
  onTranslationsChange(edits: TranslationEdit[]): void {
    const model = this.detailComponent?.detailModel as { translations?: unknown[] };
    if (!model) {
      return;
    }
    if (this.originalTranslations === undefined) {
      this.originalTranslations = model.translations ?? [];
    }
    model.translations = edits.length ? edits : this.originalTranslations;
    this.detailComponent.notifyDirtyRecheck();
  }

  private get detailComponent() {
    return (this.formState as IFormState)?.detailComponent;
  }
}
